import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTracker } from '@dpdgroupuk/react-event-tracker';
import { flatten } from 'ramda';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import palette from '../../styles/palette';
import RightDrawerModal from '../../RightDrawerModal';
import ModalTable from '../../common/Table/ModalTable';
import {
  AFFECTED_STOPS,
  CONTINUE_SPLIT_WARNING,
  DISMISS,
  SELECT_ALL_REMAINING_PARCELS,
  SELECTED_PARCELS,
  SELECTED_STOPS,
  TRANSFER_AND_OPTIMISE,
  TRANSFER_AND_SCHEDULE,
  TRANSFER_HIGH_VALUE_PARCEL,
  TRANSFER_ONLY,
  TRANSFER_PARCELS,
  TRANSFER_SPLIT_STOP_WARNING,
  TRANSFER_STOP,
  UNABLE_TO_TRANSFER_TOTE_PARCELS,
  UNABLE_TRANSFER_WITH_OPEN_REQUESTS,
} from '../../../constants/strings';
import SelectDriverRoute from '../DriverRequests/components/SelectDriverRoute';
import useLocationQueryParams from '../../../hooks/useLocationQueryParams';
import { getAvailableRoutesTransfer } from '../../../store/actions/request';
import selectedStopsTableProps from '../../../features/transferTo/selectedStopsTableProps';
import selectedParcelsTableProps from '../../../features/transferTo/selectedParcelsTableProps';
import {
  bodyCellStyles,
  headerCellStyles,
} from '../../../features/request/tableStyles';
import {
  getAffectedTransferStops,
  onTransferMixed,
  processTransfer,
} from '../../../store/actions/ui/routeDetail/transferTo';
import { showNotification } from '../../../store/actions/notifications';
import { NotificationType } from '../../../features/Notifier';
import {
  getRouteDetail,
  getUnprocessedRequest,
  isRouteReadyOrOpen,
} from '../../../store/selectors/route';
import { isRouteLocked } from './utils';
import {
  TRANSFER_MODAL,
  TRANSFER_STOP_MODAL,
} from '../../../constants/analytics';
import { useOverlay } from '../../Overlay';
import { hasHighValueParcel } from '../../../models/parcel';
import StaticBanner from '../../StaticBanner';
import TransferActionButtons from './components/TransferActionButtons';
import { setScheduleRequired } from '../../../store/reducers/route';
import {
  getRemainingParcels,
  getSelectedStops,
  getUniqueRoutesFromSelectedParcels,
  getUniqueRoutesFromSelectedStops,
} from '../../../store/selectors/ui/routeDetail';
import {
  allowToSplitStop,
  getAffectedConsignmentStops,
  getAffectedToteStops,
  getSelectedToteLabelNumbers,
  hasRouteUnprocessedRequest,
  includesToteLabelNumber,
  isDisableToteTransfer,
} from '../../../models/editStop';
import ConsignmentRestrictions from '../../../features/ConsignmentRestrictions';
import WarningBanner from '../../WarningBanner';
import { getAffectedStops } from '../../../store/actions/ui/routeDetail/removeParcels';
import affectedStopsTableProps from '../RemoveParcels/affectedStopsTableProps';
import { getErrorMessage } from '../../../utils/errors';
import {
  allowConsignmentSplitting,
  allowToteTransfers,
} from '../../../store/selectors/app';
import useSelectRouteHandler from '../DriverRequests/useSelectRouteHandler';
import StaticBannerWithConfirmActions from '../../StaticBanner/StaticBannerWithConfirmActions';

const useStyles = makeStyles(() => ({
  overlay: {
    display: 'flex',
    backgroundColor: palette.typography.black.inactive,
    position: 'absolute',
    zIndex: 9999,
    top: '19px',
    bottom: 0,
    right: 0,
    left: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  },
  moveUp: {
    marginTop: '-40px',
  },
  content: {
    position: 'relative',
    flex: 1,
  },
}));

const TransferTo = ({ open, onClose, onConfirm, isStopsSelected }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const tracker = useTracker();
  const { showOverlayWhile } = useOverlay();

  const [routes, setRoutes] = useState([]);
  const [affectedStops, setAffectedStops] = useState([]);
  const [showTransferSplitMessage, setShowTransferSplitMessage] =
    useState(false);

  const [selectedRoute, onSelectRoute, setAvailableRoutes] =
    useSelectRouteHandler();

  const query = useLocationQueryParams();
  const uniqueNextWaveRoutes = useSelector(
    isStopsSelected
      ? getUniqueRoutesFromSelectedStops
      : getUniqueRoutesFromSelectedParcels
  );
  const route = useSelector(getRouteDetail);
  const selectedStops = useSelector(getSelectedStops);
  const routeReadyOrOpen = useSelector(isRouteReadyOrOpen);
  // TODO move to hook
  const remainingParcels = useSelector(getRemainingParcels);
  const unprocessedRequests = useSelector(getUnprocessedRequest);
  const allowDepotConsignmentSplitting = useSelector(allowConsignmentSplitting);
  const allowDepotToteTransfers = useSelector(allowToteTransfers);

  const { depot, date } = query;

  const modalTitle = isStopsSelected ? TRANSFER_STOP : TRANSFER_PARCELS;
  const tableTitle = isStopsSelected ? SELECTED_STOPS : SELECTED_PARCELS;

  useEffect(() => {
    const fetchRoutes = async () => {
      const availableRoutes = await dispatch(
        getAvailableRoutesTransfer(depot, date, uniqueNextWaveRoutes)
      );
      setRoutes(availableRoutes);
      setAvailableRoutes(availableRoutes);
    };
    fetchRoutes();
  }, []);

  const getTableProps = useCallback(() => {
    const config = isStopsSelected
      ? selectedStopsTableProps
      : selectedParcelsTableProps;
    return config(headerCellStyles(), bodyCellStyles());
  }, [isStopsSelected]);

  const tableProps = getTableProps();

  const getAffectedTableProps = useCallback(
    () =>
      affectedStopsTableProps(
        headerCellStyles(),
        bodyCellStyles(),
        affectedStops
      ),
    [affectedStops]
  );

  const affectedTableProps = getAffectedTableProps();

  const selectedParcels = useMemo(
    () =>
      isStopsSelected
        ? flatten(selectedStops.map(({ parcel }) => parcel))
        : tableProps.data,
    [tableProps.data, isStopsSelected, selectedStops]
  );

  const toteParcelsCondition = useMemo(() => {
    const selectedToteLabelNumbers =
      getSelectedToteLabelNumbers(selectedParcels);
    const selectedParcelCodes = selectedParcels.map(
      ({ parcelCode }) => parcelCode
    );
    return includesToteLabelNumber(
      selectedToteLabelNumbers,
      selectedParcelCodes
    );
  }, [selectedParcels]);

  const showHighValueMessage = useMemo(
    () => hasHighValueParcel(tableProps.data),
    [tableProps.data]
  );

  const allowedSplitStop = useMemo(() => {
    const selectedParcels = tableProps.data;
    return (
      allowDepotConsignmentSplitting ||
      isStopsSelected ||
      allowToSplitStop(selectedParcels, remainingParcels)
    );
  }, [
    tableProps.data,
    isStopsSelected,
    allowDepotConsignmentSplitting,
    remainingParcels,
  ]);

  const disableToteTransfer = useMemo(
    () =>
      !allowDepotToteTransfers &&
      isDisableToteTransfer(selectedParcels, route) &&
      !affectedStops.length,
    [route, selectedParcels, affectedStops, allowDepotToteTransfers]
  );

  const isActionBtnDisabled = useMemo(
    () =>
      !allowedSplitStop ||
      disableToteTransfer ||
      !selectedRoute ||
      isRouteLocked(routes, selectedRoute),
    [selectedRoute, routes, allowedSplitStop, disableToteTransfer]
  );

  const onChange = useCallback(
    async (value) => {
      onSelectRoute(value);
      tracker.logEvent({ actionId: TRANSFER_MODAL.SELECT_ROUTE, value });
    },
    [onSelectRoute, tracker]
  );

  const addAllToteParcels = useCallback(
    showOverlayWhile(async () => {
      try {
        const processedStops = await dispatch(
          getAffectedStops({ condition: toteParcelsCondition })
        );
        const conditions = allowDepotConsignmentSplitting
          ? [getAffectedToteStops]
          : [getAffectedConsignmentStops, getAffectedToteStops];
        const affected = await dispatch(
          getAffectedTransferStops(processedStops, conditions)
        );
        setAffectedStops(affected);
      } catch (e) {
        dispatch(
          showNotification({
            key: 'transfer-unprocessed-request',
            type: NotificationType.ACTION,
            message: getErrorMessage(e),
            buttonText: DISMISS,
          })
        );
      }
    }),
    [dispatch, showOverlayWhile, toteParcelsCondition]
  );

  const processTransferToRoute = useCallback(
    async (optimise, transferOnly = false) => {
      if (hasRouteUnprocessedRequest(unprocessedRequests, affectedStops)) {
        return dispatch(
          showNotification({
            key: 'transfer-unprocessed-request',
            type: NotificationType.ACTION,
            message: UNABLE_TRANSFER_WITH_OPEN_REQUESTS,
            buttonText: DISMISS,
          })
        );
      }
      const data = await dispatch(
        affectedStops.length
          ? onTransferMixed(selectedRoute, affectedStops)
          : processTransfer(isStopsSelected, selectedRoute)
      );
      if (data) {
        transferOnly && dispatch(setScheduleRequired(true));
        onConfirm({ optimise, transferOnly });
      }
    },
    [
      unprocessedRequests,
      affectedStops,
      dispatch,
      selectedRoute,
      isStopsSelected,
      onConfirm,
    ]
  );

  const onSchedule = useCallback(async () => {
    tracker.logEvent(TRANSFER_STOP_MODAL.CLICK_SCHEDULE);
    await processTransferToRoute(false);
  }, [processTransferToRoute, tracker]);

  const onOptimise = useCallback(async () => {
    tracker.logEvent(TRANSFER_STOP_MODAL.CLICK_OPTIMISE);
    await processTransferToRoute(true);
  }, [processTransferToRoute, tracker]);

  const onTransferOnly = useCallback(async () => {
    // TODO add action ID
    await processTransferToRoute(false, true);
  }, [processTransferToRoute]);

  const transferButtonsConfig = useMemo(
    () => ({
      transfer: {
        isDisabled: isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.orange,
          border: palette.modalButtons.greenBorder,
        },
        title: TRANSFER_ONLY,
        onClick: showOverlayWhile(onTransferOnly),
      },
      optimise: {
        isDisabled: !routeReadyOrOpen || isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.green,
          border: palette.modalButtons.greenBorder,
        },
        title: TRANSFER_AND_OPTIMISE,
        onClick: showOverlayWhile(onOptimise),
      },
      schedule: {
        isDisabled: isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.blue,
          border: palette.btn.blueBorder,
        },
        title: TRANSFER_AND_SCHEDULE,
        onClick: showOverlayWhile(onSchedule),
      },
    }),
    [
      routeReadyOrOpen,
      isActionBtnDisabled,
      onOptimise,
      onSchedule,
      onTransferOnly,
      showOverlayWhile,
    ]
  );

  useEffect(() => {
    if (affectedStops.length) {
      const { stop } = route;
      const isPartialTransfer = !!affectedStops.find((affectedStop) => {
        const originalStop = stop.find(
          ({ stopId: id }) => id === affectedStop.stopId
        );
        return (
          originalStop &&
          originalStop.parcel?.length !== affectedStop.parcel.length
        );
      });
      return setShowTransferSplitMessage(isPartialTransfer);
    }
    !isStopsSelected && setShowTransferSplitMessage(!!remainingParcels.length);
  }, [isStopsSelected, remainingParcels.length, affectedStops]);

  const warningMessages = useMemo(
    () =>
      !allowedSplitStop ||
      (allowedSplitStop && disableToteTransfer) ||
      showHighValueMessage ||
      showTransferSplitMessage,
    [
      allowedSplitStop,
      disableToteTransfer,
      showHighValueMessage,
      showTransferSplitMessage,
    ]
  );

  return (
    <RightDrawerModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      loadId={TRANSFER_MODAL.LOAD}
      actions={<TransferActionButtons config={transferButtonsConfig} />}
    >
      <div className={styles.container}>
        <div className={styles.messages}>
          {!allowedSplitStop && (
            <ConsignmentRestrictions
              selectedParcels={selectedParcels}
              remainingParcels={remainingParcels}
            />
          )}
          {allowedSplitStop && disableToteTransfer && (
            <WarningBanner
              message={UNABLE_TO_TRANSFER_TOTE_PARCELS}
              onActionClick={addAllToteParcels}
              actionTitle={SELECT_ALL_REMAINING_PARCELS}
            />
          )}
          {showHighValueMessage && (
            <StaticBanner message={TRANSFER_HIGH_VALUE_PARCEL} />
          )}
          {showTransferSplitMessage && (
            <StaticBannerWithConfirmActions
              onContinue={() => setShowTransferSplitMessage(false)}
              onCancel={onClose}
              message={`${TRANSFER_SPLIT_STOP_WARNING} ${CONTINUE_SPLIT_WARNING}`}
            />
          )}
        </div>
        <div className={clsx(styles.content, warningMessages && styles.moveUp)}>
          {showTransferSplitMessage && <div className={styles.overlay} />}
          <ModalTable title={tableTitle} {...tableProps} />
          {allowedSplitStop && !disableToteTransfer && (
            <>
              {!!affectedStops.length && (
                <ModalTable title={AFFECTED_STOPS} {...affectedTableProps} />
              )}
              <SelectDriverRoute
                options={routes}
                onChange={onChange}
                value={selectedRoute}
                disabled={showTransferSplitMessage}
              />
            </>
          )}
        </div>
      </div>
    </RightDrawerModal>
  );
};

export default memo(TransferTo);
